import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { TutorialProvider } from "@/lib/tutorial";
import { Toaster } from "sonner";
import "@/App.css";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import GoogleCallback from "@/pages/GoogleCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Dashboard from "@/pages/Dashboard";
import StudyCreate from "@/pages/StudyCreate";
import StudyList from "@/pages/StudyList";
import StudyDetail from "@/pages/StudyDetail";
import DailyReview from "@/pages/DailyReview";
import Achievements from "@/pages/Achievements";
import Leaderboard from "@/pages/Leaderboard";
import Billing from "@/pages/Billing";
import Gems from "@/pages/Gems";
import PackView from "@/pages/PackView";
import MyPacks from "@/pages/MyPacks";
import Explore from "@/pages/Explore";
import Contact from "@/pages/Contact";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import RefundPolicy from "@/pages/RefundPolicy";
import CookiePolicy from "@/pages/CookiePolicy";
import Disclaimer from "@/pages/Disclaimer";
import Grievance from "@/pages/Grievance";
import CookieBanner from "@/components/CookieBanner";
import { RouteTracker } from "@/lib/track";
import NotFound from "@/pages/NotFound";
import AdminDashboard from "@/pages/AdminDashboard";
import TwoFactorSetup from "@/pages/TwoFactorSetup";
import Courses from "@/pages/Courses";
import CourseBuilder from "@/pages/CourseBuilder";
import CourseDetail from "@/pages/CourseDetail";
import StudyTechniques from "@/pages/StudyTechniques";
import StudyRooms from "@/pages/StudyRooms";
import RoomSession from "@/pages/RoomSession";
import Settings from "@/pages/Settings";
import CreatorEarnings from "@/pages/CreatorEarnings";
import AppShell from "@/components/AppShell";
import AddToHomeScreen from "@/components/AddToHomeScreen";
import ErrorBoundary from "@/components/ErrorBoundary";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] text-[#86868B] flex items-center justify-center text-[15px]">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <AppShell>{children}</AppShell>;
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/app" replace />;
  return children;
};

function App() {
  return (
    <div className="App bg-[#0A0A0B] min-h-screen">
      <ThemeProvider>
      <AuthProvider>
      <TutorialProvider>
        <AddToHomeScreen />
        <Toaster
          theme="dark"
          position="top-center"
          toastOptions={{
            style: {
              background: "rgba(19, 19, 22, 0.92)",
              backdropFilter: "saturate(180%) blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.10)",
              borderRadius: "14px",
              color: "#F4F4F5",
              fontFamily: "'Inter', sans-serif",
              fontSize: "14px",
              boxShadow: "0 10px 30px -10px rgba(10, 132, 255, 0.25)",
            },
          }}
        />
        <BrowserRouter>
          <ErrorBoundary>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/packs/:slug" element={<PackView />} />
            <Route path="/explore" element={<Explore />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/privacy-policy" element={<Privacy />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/grievance" element={<Grievance />} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />
            <Route path="/auth/google" element={<GoogleCallback />} />
            <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />

            <Route path="/app" element={<Protected><Dashboard /></Protected>} />
            <Route path="/app/studies" element={<Protected><StudyList /></Protected>} />
            <Route path="/app/studies/new" element={<Protected><StudyCreate /></Protected>} />
            <Route path="/app/studies/:id" element={<Protected><StudyDetail /></Protected>} />
            <Route path="/app/review" element={<Protected><DailyReview /></Protected>} />
            <Route path="/app/awards" element={<Protected><Achievements /></Protected>} />
            <Route path="/app/leaderboard" element={<Protected><Leaderboard /></Protected>} />
            <Route path="/app/billing" element={<Protected><Billing /></Protected>} />
            <Route path="/app/gems" element={<Protected><Gems /></Protected>} />
            <Route path="/app/my-packs" element={<Protected><MyPacks /></Protected>} />
            <Route path="/app/courses" element={<Protected><Courses /></Protected>} />
            <Route path="/app/courses/new" element={<Protected><CourseBuilder /></Protected>} />
            <Route path="/app/courses/:id" element={<Protected><CourseDetail /></Protected>} />
            <Route path="/app/security" element={<Protected><TwoFactorSetup /></Protected>} />
            <Route path="/app/settings" element={<Protected><Settings /></Protected>} />
            <Route path="/app/earnings" element={<Protected><CreatorEarnings /></Protected>} />
            <Route path="/app/explore" element={<Protected><Explore embedded /></Protected>} />
            <Route path="/app/studies/:id/techniques" element={<Protected><StudyTechniques /></Protected>} />
            <Route path="/app/rooms" element={<Protected><StudyRooms /></Protected>} />
            <Route path="/app/rooms/:code" element={<Protected><RoomSession /></Protected>} />
            <Route path="/admin" element={<Protected><AdminDashboard /></Protected>} />

            {/* Legacy redirects */}
            <Route path="/app/topics" element={<Navigate to="/app/studies" replace />} />
            <Route path="/app/topics/new" element={<Navigate to="/app/studies/new" replace />} />
            <Route path="/app/topics/:id" element={<Navigate to="/app/studies" replace />} />
            <Route path="/app/subcourses" element={<Navigate to="/app/studies" replace />} />
            <Route path="/app/subcourses/new" element={<Navigate to="/app/studies/new" replace />} />
            <Route path="/app/workspaces" element={<Navigate to="/app/studies" replace />} />
            <Route path="/app/spaces" element={<Navigate to="/app/studies" replace />} />
            <Route path="/app/spaces/:id" element={<Navigate to="/app/studies" replace />} />
            <Route path="/app/achievements" element={<Navigate to="/app/awards" replace />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
          <CookieBanner />
          <RouteTracker />
          </ErrorBoundary>
        </BrowserRouter>
      </TutorialProvider>
      </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
